import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import firebase from 'firebase/compat';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';

import { environment } from '../../../environments';
import { Service, UploadOverallTracker } from '../../models';
import { SnackbarService } from './snackbar.service';
import UploadTask = firebase.storage.UploadTask;

@Injectable()
export class JobUploaderService {
  private jobUploaderSource = new BehaviorSubject<boolean>(false);
  currentJobUploader = this.jobUploaderSource.asObservable();
  private selectedUploaderJob: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private currentUploads: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private _currentUploadTasks$: BehaviorSubject<{
    [uploadUid: string]: UploadTask
  }> = new BehaviorSubject<{
    [uploadUid: string]: UploadTask
  }>({});

  onlineEvent: Observable<Event>;
  offlineEvent: Observable<Event>;

  constructor(private http: HttpClient, private snackbarService: SnackbarService) {
    this.onlineEvent = fromEvent(window, 'online');

    this.onlineEvent
      .subscribe(ev => {
        const currentUploadTasks = Object.values(this._currentUploadTasks$.value);

        if (currentUploadTasks.length) {
          this.snackbarService.showSnackbar('Connection restored! Uploads resuming.');
          currentUploadTasks.forEach(uploadTask => {
            uploadTask.resume();
          });
        } else {
          this.snackbarService.showSnackbar('Connection restored!');
        }
      });

    this.offlineEvent = fromEvent(window, 'offline');

    this.offlineEvent
      .subscribe(ev => {
        const currentUploadTasks = Object.values(this._currentUploadTasks$.value);

        if (currentUploadTasks.length) {
          this.snackbarService.showSnackbar('Seems you are offline. Uploads will continue when connection is back!');
          Object.values(this._currentUploadTasks$.value).forEach(uploadTask => {
            uploadTask.pause();
          });
        } else {
          this.snackbarService.showSnackbar('Seems you are offline. Please check your connection.');
        }
      });
  }

  deleteUploads(uploadsToDelete) {
    return this.http.post(`${environment.apiBaseUrl}/DeleteUploads`, {uploadsToDelete});
  }

  get currentUploads$(): Observable<UploadOverallTracker> {
    return this.currentUploads.asObservable();
  }

  setSelectedUploaderJob(job) {
    this.selectedUploaderJob.next(job);
  }

  setCurrentUpload(uploadTask, fileUploadProgress, fileUploadCompleted, uploadName, uploadUUID, jobId) {
    const currentUploadsTemp: any = this.currentUploads.getValue();

    if (!!uploadUUID && !!jobId) {
      if (!currentUploadsTemp[jobId]) {
        currentUploadsTemp[jobId] = {};
        currentUploadsTemp[jobId][uploadUUID] = {
          fileName: uploadName,
          progress: fileUploadProgress,
          finalized: fileUploadCompleted
        };
      } else {
        currentUploadsTemp[jobId][uploadUUID] = {
          fileName: uploadName,
          progress: fileUploadProgress,
          finalized: fileUploadCompleted
        };

        if (fileUploadCompleted) {
          delete currentUploadsTemp[jobId][uploadUUID];

          if (Object.values(currentUploadsTemp[jobId]).length === 0) {
            delete currentUploadsTemp[jobId];
          }
        }
      }

      this._currentUploadTasks$.value[uploadUUID] = uploadTask;
      this.currentUploads.next(currentUploadsTemp);
    }
  }

  deliverJob(job: Service) {
    const jobCopy = cloneDeep(job);
    delete jobCopy.assetsData;
    delete jobCopy.images;
    jobCopy.imagesToDeliver = jobCopy.imagesToDeliver.map(image => {
      delete image.url;
      return image;
    });

    return this.http.post(environment.apiBaseUrl + '/DeliverJob', jobCopy)
      .toPromise();
  }

  updateJobUploaded(value: boolean) {
    this.jobUploaderSource.next(value);
  }

}
